import { useState, useCallback, useEffect } from 'react';
import { Settings2, Check } from 'lucide-react';
import { Button } from '@/shared/ui/button/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/shared/ui/dialog/Dialog';
import { cn } from '@/shared/lib/utils';

interface PeriodComparisonDialogProps {
  availableMonths: string[];
  selectedMonths: string[];
  onChange: (months: string[]) => void;
  disabled?: boolean;
}

const MAX_SELECTION = 4;

export function PeriodComparisonDialog({
  availableMonths,
  selectedMonths,
  onChange,
  disabled,
}: PeriodComparisonDialogProps) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<string[]>(selectedMonths);

  useEffect(() => {
    setDraft(selectedMonths);
  }, [selectedMonths, open]);

  const toggleMonth = useCallback((month: string) => {
    setDraft(prev => {
      if (prev.includes(month)) {
        return prev.filter(m => m !== month);
      }
      if (prev.length >= MAX_SELECTION) return prev;
      return [...prev, month];
    });
  }, []);

  const handleApply = useCallback(() => {
    onChange(draft);
    setOpen(false);
  }, [draft, onChange]);

  const isAtLimit = draft.length >= MAX_SELECTION;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          disabled={disabled}
          className="h-9 gap-1.5 border-0 bg-transparent px-3 text-sm font-semibold text-text-primary hover:bg-surface-elevated hover:text-text-primary"
        >
          <Settings2 className="h-4 w-4 text-secondary" />
          Comparar
          <span className="ml-1 rounded-full bg-secondary px-1.5 py-0.5 text-[10px] font-bold text-text-inverse">
            {selectedMonths.length}
          </span>
        </Button>
      </DialogTrigger>

      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Comparar periodos</DialogTitle>
          <DialogDescription>
            Selecciona hasta {MAX_SELECTION} meses para comparar en el estado de
            resultados.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 px-1 py-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-text-secondary">
              {draft.length} de {MAX_SELECTION} seleccionados
            </span>
            {isAtLimit && (
              <span className="text-xs font-semibold text-danger-text">
                Límite alcanzado
              </span>
            )}
          </div>

          <div className="grid grid-cols-3 gap-2">
            {availableMonths.map(month => {
              const selected = draft.includes(month);
              const canSelect = selected || !isAtLimit;

              return (
                <button
                  key={month}
                  type="button"
                  onClick={() => toggleMonth(month)}
                  disabled={!canSelect}
                  className={cn(
                    'relative flex items-center justify-center rounded-xl border px-2 py-2.5 text-xs font-semibold transition-all',
                    selected
                      ? 'border-primary bg-primary-muted text-primary'
                      : 'border-border bg-surface text-text-secondary hover:bg-surface-elevated',
                    !canSelect && 'cursor-not-allowed opacity-50'
                  )}
                >
                  {selected && (
                    <Check className="absolute left-1.5 top-1.5 h-3 w-3" />
                  )}
                  {month}
                </button>
              );
            })}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setOpen(false)}
          >
            Cancelar
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleApply}
            disabled={draft.length === 0}
          >
            Aplicar ({draft.length})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
